import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { SolicitudSchemaApi } from './solicitud.service';
import { obtenerIdEstadoSolicitud, obtenerIdTipoSolicitud } from './solicitud.service';

/**
 * Plan de negocio asociado a un productor.
 * Esquema OpenAPI: PlanNegocio — http://apip-sat.tideapps.com:8002/openapi.json
 * id_plan_negocio y estado pueden ser null cuando el productor no tiene plan.
 * `id_estado_plan_negocio` null con o sin plan se muestra en UI como Diagnóstico (mismo filtro que id 1).
 */
export interface PlanNegocio {
  id_plan_negocio: string | null;
  estado: string | null;
  /** 1 Diagnóstico, 2 Diseño, 3 Ejecución; null equivale a Diagnóstico en la interfaz. */
  id_estado_plan_negocio?: number | string | null;
  /**
   * Solicitud de aprobación del plan: 1 Creada, 2 Aprobada, 3 Rechazada.
   * null u omitido = sin solicitud registrada en este payload.
   */
  id_estado_solicitud?: number | string | null;
}

/** Valores `id_estado_plan_negocio` alineados con la API de listados / plan. */
export const ID_ESTADO_PLAN_NEGOCIO = {
  DIAGNOSTICO: 1,
  DISENO: 2,
  EJECUCION: 3,
} as const;

/** Valores `id_estado_solicitud` para la solicitud de aprobación del plan de negocio. */
export const ID_ESTADO_SOLICITUD_PLAN = {
  CREADA: 1,
  APROBADA: 2,
  RECHAZADA: 3,
} as const;

/** Normaliza id numérico que la API puede enviar como string. */
export function normalizarIdNumericoPlanNegocio(raw: unknown): number | null {
  if (raw == null) return null;
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
  return Number.isFinite(n) ? n : null;
}

/**
 * Lee `id_estado_solicitud` desde `ultima_solicitud` (listado jerárquico), luego `plan_negocio`, luego raíz.
 */
export function obtenerIdEstadoSolicitudPlanDesdeProductor(
  item: ProductorPlanNegocioItem | null | undefined
): number | null {
  if (!item) return null;
  const ult = item.ultima_solicitud;
  if (ult?.estado != null) {
    const fromUlt = obtenerIdEstadoSolicitud(ult.estado);
    if (fromUlt != null) return fromUlt;
  }
  const fromPlan = normalizarIdNumericoPlanNegocio(item.plan_negocio?.id_estado_solicitud);
  if (fromPlan != null) return fromPlan;
  return normalizarIdNumericoPlanNegocio(
    (item as ProductorPlanNegocioItem & { id_estado_solicitud?: unknown }).id_estado_solicitud
  );
}

/** `id_tipo_solicitud` de `ultima_solicitud.tipo` (1 Aprobación, 2 Reapertura), o null. */
export function obtenerIdTipoSolicitudUltimaDesdeProductor(
  item: ProductorPlanNegocioItem | null | undefined
): number | null {
  const ult = item?.ultima_solicitud;
  if (!ult?.tipo) return null;
  return obtenerIdTipoSolicitud(ult.tipo);
}

/**
 * Deshabilita «Solicitar aprobación» solo si la solicitud está **Creada** (1) pendiente.
 * Con **Aprobada** (2), **Rechazada** (3) o sin estado se puede volver a enviar en Diseño.
 */
export function solicitudPlanBloqueaBotonSolicitarAprobacion(idEstadoSolicitud: number | null | undefined): boolean {
  const id = normalizarIdNumericoPlanNegocio(idEstadoSolicitud);
  return id === ID_ESTADO_SOLICITUD_PLAN.CREADA;
}

/** true si hay solicitud pendiente de resolución (Creada); bloquea «Solicitar reapertura» en Ejecución. */
export function solicitudPlanBloqueaSolicitarReapertura(idEstadoSolicitud: number | null | undefined): boolean {
  return solicitudPlanBloqueaBotonSolicitarAprobacion(idEstadoSolicitud);
}

/** Coordenadas (lat/lng) en ítem de listado. OpenAPI: parte de ProductorListItem. */
export interface CoordenadasItem {
  lat: number;
  lng: number;
}

/** Detalle de avance del plan (viene en listado; puede ser null). */
export interface DetalleAvancePlanNegocio {
  /** Porcentaje total 0..100 (según API). */
  total: number;
  diagnostico: number;
  soluciones: number;
  plan_de_trabajo: number;
  plan_de_inversion: number;
  margen_bruto: number;
}

/** Objeto anidado con descripción (etnia, género, jerarquía) */
export interface DescripcionItem {
  descripcion: string;
}

/** Negocio dentro de caracterización (GET caracterizacion) */
export interface NegocioCaracterizacion {
  rut: string;
  razon_social: string;
  rubro: string;
  id_rubro?: number | null;
  categoria: string;
  negocio_principal: boolean;
  objetivos_mejora: string;
}

/** Recursos suelo (caracterización) */
export interface RecursoSuelo {
  superficie_total: number;
  tenencia: string;
  tiene_otro_predio: boolean;
  superficie_productiva: number;
}

/** Recursos hídricos (caracterización) */
export interface RecursoHidrico {
  fuente: string;
  tiene_derecho: boolean;
  tenencia: string;
  sistema_riego: string;
}

/** Otros recursos (caracterización) */
export interface RecursoOtro {
  fuente_energia: string;
  trabaja_con_familiares: boolean;
  familiares_ocacionales: number;
  familiares_permanentes: number;
  trabajadores_contratados: number;
}

/** Plan de trabajo (caracterización) */
export interface PlanTrabajo {
  estado: string;
  fecha_inicio: string;
  fecha_fin: string;
  progreso: string;
  ultima_visita: string;
  proxima_visita: string;
}

/**
 * Respuesta de GET /api/v1/negocio/productor/{id_productor}/caracterizacion
 */
export interface ProductorCaracterizacion {
  id_productor: string;
  rut: string;
  nombre: string;
  primer_apellido?: string | null;
  segundo_apellido?: string | null;
  apellidos?: string;
  comuna?: string | null;
  ultima_visita?: string | null;
  coordenadas?: { lat: number; lng: number } | null;
  jerarquia?: DescripcionItem | null;
  id_estado_productor?: number | null;
  fecha_nacimiento?: string | null;
  grupo_etario?: string | null;
  etnia?: DescripcionItem | null;
  genero?: DescripcionItem | null;
  id_nivel_educativo?: number | null;
  fuente?: string | null;
  fecha_ingreso?: string | null;
  fono_contacto?: string | null;
  email_contacto?: string | null;
  domicilio?: string | null;
  antecedentes?: { creditos?: number; proyectos?: number } | null;
  negocios?: NegocioCaracterizacion[] | null;
  recursos_suelo?: RecursoSuelo | null;
  recursos_hidricos?: RecursoHidrico | null;
  recursos_otros?: RecursoOtro | null;
  plan_trabajo?: PlanTrabajo | null;
}

/**
 * Item de productor del listado paginado.
 * Esquema OpenAPI: ProductorListItem — GET /api/v1/negocio/perfil/{id_perfil}/jerarquia/{id_jerarquia}
 * Ver: http://apip-sat.tideapps.com:8002/openapi.json
 * Cada item incluye plan_negocio; si id_plan_negocio no es null, el tab Soluciones puede llamar a
 * GET .../{id_plan_negocio}/soluciones.
 */
export interface ProductorPlanNegocioItem {
  id_productor: string;
  /** Jerarquía propia del productor (viene desde GET /negocio/perfil/{id_perfil}/jerarquia/{id_jerarquia}). */
  id_jerarquia_productor: number;
  rut: string | null;
  nombre: string;
  primer_apellido: string | null;
  segundo_apellido: string | null;
  apellidos: string | null;
  unidad_operativa: string | null;
  area: string | null;
  comuna: string | null;
  email: string | null;
  ultima_visita: string | null;
  coordenadas: CoordenadasItem | null;
  plan_negocio: PlanNegocio | null;
  /** Avance por secciones del plan; puede venir null cuando no aplica/no existe. */
  detalle_avance?: DetalleAvancePlanNegocio | null;
  /** Si el API envía `id_estado_solicitud` a nivel de ítem (además de dentro de `plan_negocio`). */
  id_estado_solicitud?: number | string | null;
  /** Última solicitud (GET jerarquía); prioritaria para estado/tipo en flujo de gestión. */
  ultima_solicitud?: SolicitudSchemaApi | null;
  ultimo_mb?: string | null;
  ultimo_ev?: string | null;
  tiene_encuesta_activa?: boolean | null;
}

/**
 * Respuesta de GET /api/v1/negocio/id_productor/{id_productor}.
 * Nota: este endpoint entrega un único productor (evita usar listados genéricos con search).
 */
export interface ProductorNegocioPorIdResponse {
  id_productor: string;
  rut?: string | null;
  nombre: string;
  primer_apellido?: string | null;
  segundo_apellido?: string | null;
  apellidos?: string | null;
  unidad_operativa?: string | null;
  area?: string | null;
  comuna?: string | null;
  email?: string | null;
  ultima_visita?: string | null;
  coordenadas?: { lat: number; lng: number } | null;
  plan_negocio?: PlanNegocio | null;
  id_jerarquia_productor?: number | null;
  ultima_solicitud?: SolicitudSchemaApi | null;
  detalle_avance?: DetalleAvancePlanNegocio | null;
}

export function mapProductorNegocioPorIdAPlanNegocioItem(
  p: ProductorNegocioPorIdResponse
): ProductorPlanNegocioItem {
  return {
    id_productor: p.id_productor,
    id_jerarquia_productor: typeof p.id_jerarquia_productor === 'number' ? p.id_jerarquia_productor : -1,
    rut: p.rut ?? null,
    nombre: p.nombre,
    primer_apellido: p.primer_apellido ?? null,
    segundo_apellido: p.segundo_apellido ?? null,
    apellidos: p.apellidos ?? null,
    unidad_operativa: p.unidad_operativa ?? null,
    comuna: p.comuna ?? null,
    email: p.email ?? null,
    ultima_visita: p.ultima_visita ?? null,
    area: p.area ?? null,
    coordenadas: p.coordenadas
      ? { lat: Number(p.coordenadas.lat), lng: Number(p.coordenadas.lng) }
      : null,
    plan_negocio: p.plan_negocio ?? null,
    detalle_avance: p.detalle_avance ?? null,
    ultima_solicitud: p.ultima_solicitud ?? null,
  };
}

/**
 * Construye un ítem de gestión desde GET .../negocio/productor/{id}/caracterizacion cuando el productor
 * no aparece en el listado paginado (p. ej. visible en solicitudes pero fuera de la primera página o del filtro).
 */
export function mapProductorCaracterizacionAPlanNegocioItem(
  c: ProductorCaracterizacion
): ProductorPlanNegocioItem {
  return {
    id_productor: c.id_productor,
    id_jerarquia_productor: -1,
    rut: c.rut ?? null,
    nombre: c.nombre,
    primer_apellido: c.primer_apellido ?? null,
    segundo_apellido: c.segundo_apellido ?? null,
    apellidos: c.apellidos ?? null,
    unidad_operativa: null,
    area: null,
    comuna: c.comuna ?? null,
    email: c.email_contacto ?? null,
    ultima_visita: c.ultima_visita ?? null,
    coordenadas: c.coordenadas
      ? { lat: Number(c.coordenadas.lat), lng: Number(c.coordenadas.lng) }
      : null,
    plan_negocio: null,
    detalle_avance: null,
  };
}

/**
 * Respuesta paginada de GET /api/v1/negocio/perfil/{id_perfil}/jerarquia/{id_jerarquia}.
 * Esquema OpenAPI: Page_ProductorListItem_
 */
export interface ProductoresPlanNegocioPaginados {
  items: ProductorPlanNegocioItem[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

/**
 * Parámetros de consulta para listado de productores con plan de negocio.
 * page, size, sort, order, search; filtro opcional `id_estado_plan_negocio` (GET .../jerarquia/{id}).
 */
export interface NegocioQueryParams {
  page?: number;
  size?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  search?: string;
  id_estado_plan_negocio?: number;
  /** Si true, se envía `incluir_sin_estado_plan_negocio=true` (uso manual; el listado en gestión usa filtro en cliente para Diagnóstico). */
  incluir_sin_estado_plan_negocio?: boolean;
}

/**
 * Normaliza el valor del ion-select (a veces string) y null/NaN.
 */
export function normalizarIdEstadoPlanNegocioFiltro(
  id: number | string | null | undefined
): number | null {
  if (id == null || id === '') return null;
  const n = typeof id === 'number' ? id : Number(id);
  return Number.isNaN(n) ? null : n;
}

/**
 * Query string para el listado: Diseño/Ejecución filtran en servidor.
 * Diagnóstico (1) no envía `id_estado_plan_negocio` porque muchos backends no devuelven filas con id null al filtrar por 1; se acota en cliente con `planNegocioCoincideConFiltroEstado`.
 */
export function resolverQueryFiltroEstadoPlanNegocio(
  idSeleccionado: number | string | null | undefined
): Pick<NegocioQueryParams, 'id_estado_plan_negocio' | 'incluir_sin_estado_plan_negocio'> {
  const id = normalizarIdEstadoPlanNegocioFiltro(idSeleccionado);
  if (id == null) return {};
  if (id === ID_ESTADO_PLAN_NEGOCIO.DIAGNOSTICO) {
    return {id_estado_plan_negocio: 0};
  }
  return { id_estado_plan_negocio: id };
}

/** Ítem de GET /api/v1/negocio/obtenerEstadosNegocio */
export interface EstadoPlanNegocioOpcion {
  id_estado_plan_negocio: number;
  descripcion: string;
}

/**
 * Opción de priorización para diagnóstico.
 * OpenAPI: GET /api/v1/negocio/obtenerPriorizacion
 */
export interface PriorizacionResponse {
  id_priorizacion: number;
  descripcion: string;
}

// ── Estructura de plan de negocio (PDF “Versión actual”) ──

/** Sección Caracterización del endpoint estructura (OpenAPI: `CaracterizacionEst`). */
export interface CaracterizacionEst {
  id_plan_negocio?: string | null;
  productor?: string | null;
  rut?: string | null;
  unidad_operativa?: string | null;
  rubro_principal?: string | null;
  elaborado_por?: string | null;
  aprobado_por?: string | null;
  fecha_aprobacion?: string | null;
  estado?: string | null;
}

/** Sección Resumen de Problemas (OpenAPI: `ResumenProblemas`). */
export interface ResumenProblemasItem {
  area_gestion?: string | null;
  rubro?: string | null;
  hallazgo?: string | null;
  priorizacion?: string | number | null;
}

/** Sección Soluciones Técnicas (OpenAPI: `SolucionesTecnicas`). */
export interface SolucionesTecnicasItem {
  rubro?: string | null;
  area_gestion?: string | null;
  hallazgo?: string | null;
  categorias_pdas?: Array<{ id_categoria_pda: number; descripcion: string }> | null;
  asesoria_individual?: string | null;
  titulo?: string | null;
  asesoria_especializada?: Array<{ id_asesoria_especializada: number; nombre: string }> | null;
  requiere_inversion?: boolean | null;
}

/** Sección Plan de Trabajo (OpenAPI: `app__models__schemas__PlanTrabajo`). */
export interface PlanTrabajoEstructuraItem {
  nombre?: string | null;
  temas?: string[] | [];
  ciclo?: number | null;
  fecha_inicio?: string | null;
  fecha_termino?: string | null;
}

export interface CronogramaEstructuraItem {
  nombre?: string | null;
  tipo?: string | null;
  ciclo?: number | null;
  fechas?: Array<{ fecha_inicio: string; fecha_termino: string }> | null;
}

/** Sección Plan de Inversión (OpenAPI: `PlanInversion`). */
export interface PlanInversionEstructuraItem {
  rubro?: string | null;
  solucion?: string | null;
  descripcion_inversion?: string | null;
  tipo?: string | null;
  costo_total?: number | string | null;
  cronograma?: string | null;
}

/** Ítem de GET /api/v1/negocio/{id_plan_negocio}/listaPdf (versiones históricas para PDF). */
export interface ListaPdfPlanNegocioItem {
  fecha: string;
  descripcion: string;
}

/** Opciones para GET .../estructura cuando se consulta una versión archivada por fecha. */
export interface ObtenerEstructuraPlanNegocioOpts {
  /** ISO 8601; debe coincidir con `fecha` devuelta por listaPdf. */
  fecha?: string;
}

/** Respuesta de `GET /api/v1/negocio/{id_plan_negocio}/estructura` (OpenAPI: `EstructuraPlan`). */
export interface EstructuraPlan {
  /** Nuevo contrato: propiedades de caracterización vienen planas en la raíz (Swagger SAT 2026-05). */
  id_plan_negocio?: string | null;
  productor?: string | null;
  rut?: string | null;
  unidad_operativa?: string | null;
  rubro_principal?: string | null;
  elaborado_por?: string | null;
  aprobado_por?: string | null;
  fecha_aprobacion?: string | null;
  estado?: string | null;

  agencia_area: string | null;
  inicio_elaboracion: string | null;
  ultima_fecha_envio_aprobacion: string | null;
  perfil_aprobador: string | null;

  /** Compat legado: algunos entornos/versiones aún envían `caracterizacion` anidada. */
  caracterizacion?: CaracterizacionEst | null;
  resumen_problemas?: ResumenProblemasItem[] | null;
  soluciones_tecnicas?: SolucionesTecnicasItem[] | null;
  plan_trabajo?: PlanTrabajoEstructuraItem[] | null;
  cronograma?: CronogramaEstructuraItem[] | null;
  plan_inversion?: PlanInversionEstructuraItem[] | null;
}

/**
 * Categoría de asesoría para soluciones (plan trabajo / plan inversión).
 * Si el API no envía esta propiedad, se trata como "Sin categoría".
 */
export type TipoAsesoria = 'tecnica' | 'especializada';

/** Rubro asociado a una solución (ej. Hortalizas, Leguminosas de Grano). */
export interface RubroSolucion {
  id_rubro: number;
  descripcion: string;
  fecha_envio?: string;
}

/** Opción para el selector de categorías PDA. */
export interface CategoriaPdaItem {
  id_categoria_pda: number;
  descripcion: string;
  /**
   * Algunos endpoints/versiones pueden incluir el id del registro de asignación.
   * Se usa para compatibilidad con selección por `id_pda_asignado_solucion`.
   */
  id_pda_asignado_solucion?: number;
}

/** Registro de categoría PDA asignada a una solución. */
export interface PdaAsignadoSolucion {
  id_pda_asignado_solucion: number;
  id_solucion: string;
  id_categoria_pda: number;
  pda?: { id_categoria_pda: number; descripcion: string } | null;
}

/** Opción para el selector de asesorías especializadas. */
export interface ActividadGrupalOpcionSoluciones {
  id_asesoria_especializada: number;
  nombre: string;
  fecha: string | null;
  detalle: Object | null;
  /**
   * Algunos endpoints/versiones pueden incluir el id del registro de asignación.
   * Se usa para compatibilidad con selección por `id_asesoria_especializada_asignada_solucion`.
   */
  id_asesoria_especializada_asignada_solucion?: number;
}

/** Detalle (o forma reducida) de una asesoría especializada dentro de una asignación. */
export interface ActividadGrupalDetalle {
  id_asesoria_especializada: number;
  id_jerarquia: number;
  id_categoria_asesoria_especializada: number;
  nombre: string;
  objetivos: string | null;
  asesor: string | null;
  lugar: string | null;
  fecha: string;
}

/** Registro de asesoría especializada asignada a una solución. */
export interface ActividadGrupalAsignadaSolucion {
  id_asesoria_especializada_asignada_solucion: number;
  id_solucion: string;
  id_asesoria_especializada: number;
  asesoriaEspecializada?: ActividadGrupalDetalle | null;
}

/** Payload de actualización de una solución para PUT bulk de plan de negocio. */
export interface SolucionUpdateRequest {
  id_solucion: string;
  requiere_inversion: boolean;
  es_individual: boolean;
  observaciones: string;
  titulo: string;
  //pda_ids: number[];
  asesorias_ids: number[];
}

/** Payload de PUT /api/v1/negocio/{id_plan_negocio}/soluciones */
export interface BulkUpdateSolucionesRequest {
  id_plan_negocio: string;
  soluciones: SolucionUpdateRequest[];
}

/**
 * Tipo de inversión (OpenAPI: TipoInversionSchema).
 */
export interface TipoInversionSchema {
  id: number;
  descripcion: string;
}

/**
 * Detalle persistible del plan de inversión (OpenAPI: PlanInversionSchema).
 * Algunas respuestas anidan los campos bajo `plan_inversion`.
 */
export interface PlanInversionSchema {
  descripcion: string;
  tipo: TipoInversionSchema;
  costo_total: number;
  /**
   * Cronograma como lista de años relativos (1..4).
   * Compat: el backend antes enviaba máscara de bits (number); el extractor tolera ambos.
   */
  cronograma: number[];
  /**
   * Ruta interna para `GET /api/v1/archivos/{file_path}` (antes a veces mezclada con nombre de archivo).
   * Respuesta nueva: coincide con `ruta_almacenamiento` del objeto `documento_plan`.
   */
  documento_plan?: string | null;
  /** Nombre legible del plan (GET nuevo: `nombre_archivo`); si falta se deriva de la ruta. */
  documento_plan_nombre?: string | null;
  /** GET soluciones: texto CSV o un solo path (legacy). */
  documento_anexo?: string | null;
  /** GET soluciones: lista de rutas cuando el API expone varios anexos como array (p. ej. tras `documentos_anexos` en PUT). */
  documentos_anexos?: string[] | null;
}

/**
 * Metadato de adjunto de plan de inversión (GET `detalle_inversion` / multipart `documentos_anexos_anteriores`).
 */
export interface DocumentoInversionAdjuntoApi {
  mime_type?: string | null;
  fecha_subida?: string | null;
  tamano_bytes?: number | null;
  nombre_archivo: string;
  ruta_almacenamiento: string;
}

/**
 * Cuerpo JSON esperado por el backend en multipart (`documento_plan_anterior`, `documentos_anexos_anteriores`).
 */
export function serializarAdjuntoInversionFormBody(doc: DocumentoInversionAdjuntoApi): string {
  const tam =
    typeof doc.tamano_bytes === 'number' && Number.isFinite(doc.tamano_bytes)
      ? Math.round(doc.tamano_bytes)
      : 0;
  return JSON.stringify({
    mime_type: doc.mime_type != null ? String(doc.mime_type) : '',
    fecha_subida: doc.fecha_subida != null ? String(doc.fecha_subida) : '',
    tamano_bytes: tam,
    nombre_archivo: doc.nombre_archivo != null ? String(doc.nombre_archivo) : '',
    ruta_almacenamiento: doc.ruta_almacenamiento != null ? String(doc.ruta_almacenamiento) : '',
  });
}

/**
 * PUT /api/v1/negocio/actualizaInversion — operación «Bulk Guardar Inversion» (`bulk_guardar_inversion_api_v1_negocio_actualizaInversion_put`).
 * Query: `id_solucion` requerido. Opcionales (nullable): `descripcion`, `id_tipo`, `tipo`, `costo_total`.
 * Body multipart (`Body_bulk_guardar_inversion_api_v1_negocio_actualizaInversion_put`): `cronograma` (array[int] | null),
 * opcionales `documento_plan`, `documentos_anexos` (array de ficheros) + anteriores.
 * @see http://apip-sat.tideapps.com:8002/openapi.json
 */
export interface ActualizaInversionPutParams {
  id_solucion: string;
  descripcion?: string | null;
  id_tipo?: number | null;
  tipo?: string | null;
  costo_total?: number | null;
  /** Lista de años relativos (1..4). Se envía como múltiples campos `cronograma` en el FormData. */
  cronograma?: number[] | null;
  /**
   * Documento plan ya almacenado que se conserva: en `FormData` se envía como **string** JSON del objeto
   * (no `Blob`), para que el navegador no añada `filename` y FastAPI pueda tratarlo como campo de formulario.
   * `undefined`: omitir. `null`: enviar el literal `null`.
   */
  documento_plan_anterior?: DocumentoInversionAdjuntoApi | null;
  documento_plan?: File | null;
  /**
   * Anexos anteriores: varias entradas `documentos_anexos_anteriores`, cada una **string** JSON de un `documento`
   * (mismo patrón que `requests` con varios valores y `List[str] = Form()` + `model_validate_json` en el API).
   */
  documentos_anexos_anteriores?: DocumentoInversionAdjuntoApi[] | null;
  /** Misma petición: varias partes `documentos_anexos` en el FormData (OpenAPI: array de binary). */
  documentos_anexos?: File[] | null;
}

/**
 * Ítem de PUT /api/v1/negocio/actualizaFechasPlan.
 * El servicio acepta `null` en fechas para ítems sin fecha o borrado de fechas.
 */
export interface SolucionFechaRequest {
  id_solucion: string;
  fecha_inicio: string | null;
  fecha_termino: string | null;
}

export interface VisitaFechaRequest {
  id_visita: string | null;
  nombre: string | null;
  lista_soluciones: string[] | null;
  id_tipo_visita: number | null;
  fecha_inicio: string | null;
  fecha_termino: string | null;
  ciclo: number;
}

/** Body de PUT /api/v1/negocio/actualizaFechasPlan (OpenAPI: BulkFechasRequest). */
export interface BulkFechasRequest {
  id_plan_negocio: string;
  soluciones: SolucionFechaRequest[];
}

export interface BulkFechasVisitasRequest {
  id_plan_negocio: string;
  visitas: VisitaFechaRequest[];
}

/** Costo en JSON a veces es string con separadores (ej. miles); alinea con el formulario. */
function normalizarCostoPlanApi(raw: unknown): number | undefined {
  if (raw == null) return undefined;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return Math.round(raw);
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    const soloDigitos = raw.replace(/\D/g, '');
    if (soloDigitos !== '') {
      const n = Number(soloDigitos);
      return Number.isFinite(n) ? Math.min(Math.round(n), Number.MAX_SAFE_INTEGER) : undefined;
    }
    const n = Number(raw.trim().replace(',', '.'));
    return Number.isFinite(n) ? Math.round(n) : undefined;
  }
  return undefined;
}

/** `id_tipo` en JSON a veces llega como string; normaliza a entero. */
function parseIdTipoInversionApi(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return Math.round(raw);
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw.trim());
    return Number.isFinite(n) ? Math.round(n) : null;
  }
  return null;
}

function normalizarCronogramaApi(raw: unknown): number[] | null {
  // Nuevo formato OpenAPI: array[int] (años relativos 1..4).
  if (Array.isArray(raw)) {
    const out = raw
      .map((x) => (typeof x === 'number' ? x : Number(x)))
      .filter((n) => Number.isFinite(n))
      .map((n) => Math.round(n))
      .filter((n) => n >= 1 && n <= 4);
    // unique + stable sort
    return Array.from(new Set(out)).sort((a, b) => a - b);
  }

  // Compat: formato legado como máscara de bits (number/string).
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return null;
  const mask = Math.max(0, Math.round(n));
  const years: number[] = [];
  for (let i = 0; i < 4; i++) {
    if (mask & (1 << i)) years.push(i + 1);
  }
  return years.length ? years : null;
}

/**
 * Solo el objeto `tipo` dentro de `detalle_inversion` (no exige descripción del plan ni costo/cronograma).
 * Sirve cuando el GET trae un detalle parcial o el parser estricto rechaza el bloque completo.
 */
export function extraerTipoInversionDesdeDetalle(detalle: unknown): TipoInversionSchema | null {
  if (detalle == null || typeof detalle !== 'object') return null;
  const root = detalle as Record<string, unknown>;
  const nested = root['plan_inversion'] ?? root['planInversion'];
  const plan =
    nested != null && typeof nested === 'object'
      ? (nested as Record<string, unknown>)
      : root;
  const tipoRaw = plan['tipo'];
  if (tipoRaw == null || typeof tipoRaw !== 'object') return null;
  const tipoObj = tipoRaw as Record<string, unknown>;
  const idTipo = parseIdTipoInversionApi(tipoObj['id']);
  const descTipo = tipoObj['descripcion'];
  if (idTipo == null || typeof descTipo !== 'string' || !descTipo.trim()) return null;
  return { id: idTipo, descripcion: descTipo.trim() };
}

/** Campos opcionales del plan leídos con criterios relajados (cada uno si está presente y es válido). */
export function extraerPlanInversionParcialDesdeDetalle(detalle: unknown): {
  descripcion?: string;
  tipo?: TipoInversionSchema;
  costo_total?: number;
  cronograma?: number[];
} {
  const out: {
    descripcion?: string;
    tipo?: TipoInversionSchema;
    costo_total?: number;
    cronograma?: number[];
  } = {};
  if (detalle == null || typeof detalle !== 'object') return out;
  const root = detalle as Record<string, unknown>;
  const nested = root['plan_inversion'] ?? root['planInversion'];
  const plan =
    nested != null && typeof nested === 'object'
      ? (nested as Record<string, unknown>)
      : root;
  const descRaw = plan['descripcion'];
  if (typeof descRaw === 'string' && descRaw.trim()) {
    out.descripcion = descRaw.trim();
  }
  const tipo = extraerTipoInversionDesdeDetalle(detalle);
  if (tipo) {
    out.tipo = tipo;
  }
  const costoNorm = normalizarCostoPlanApi(plan['costo_total']);
  if (costoNorm != null) {
    out.costo_total = costoNorm;
  }
  const cronRaw = plan['cronograma'];
  const cron = normalizarCronogramaApi(cronRaw);
  if (cron && cron.length) {
    out.cronograma = cron;
  }
  return out;
}

/** Ruta/nombre de un único documento plan (string o primer elemento si el API envía lista). */
function normalizarReferenciaDocumentoPlan(raw: unknown): string | null {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const t = raw.trim();
    return t || null;
  }
  if (Array.isArray(raw) && raw.length > 0) {
    const t = String(raw[0] ?? '').trim();
    return t || null;
  }
  return null;
}

function nombreArchivoDesdeRutaAlmacenamiento(ruta: string): string {
  const t = ruta.trim();
  if (!t) return t;
  const parts = t.split('/').filter(Boolean);
  return parts.length ? (parts[parts.length - 1] ?? t) : t;
}

function parseTamanoBytesApi(raw: unknown): number | null {
  if (raw == null) return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return Math.round(raw);
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw.trim());
    return Number.isFinite(n) ? Math.round(n) : null;
  }
  return null;
}

/**
 * GET `detalle_inversion`: `documento_plan` / ítems de `documentos_anexos` como metadata
 * (`nombre_archivo`, `ruta_almacenamiento`, etc.).
 */
function adjuntoInversionMetadataDesdeObjetoApi(raw: unknown): DocumentoInversionAdjuntoApi | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const rutaRaw = o['ruta_almacenamiento'] ?? o['rutaAlmacenamiento'];
  const ruta = typeof rutaRaw === 'string' ? rutaRaw.trim() : '';
  if (!ruta) return null;
  const nomRaw = o['nombre_archivo'] ?? o['nombreArchivo'];
  const nombre_archivo =
    typeof nomRaw === 'string' && nomRaw.trim()
      ? nomRaw.trim()
      : nombreArchivoDesdeRutaAlmacenamiento(ruta);
  const mimeRaw = o['mime_type'] ?? o['mimeType'];
  const fechaRaw = o['fecha_subida'] ?? o['fechaSubida'];
  const tamRaw = o['tamano_bytes'] ?? o['tamanoBytes'];
  return {
    ruta_almacenamiento: ruta,
    nombre_archivo,
    mime_type: typeof mimeRaw === 'string' && mimeRaw.trim() ? mimeRaw.trim() : null,
    fecha_subida: typeof fechaRaw === 'string' && fechaRaw.trim() ? fechaRaw.trim() : null,
    tamano_bytes: parseTamanoBytesApi(tamRaw),
  };
}

function adjuntoMinimoDesdeRutaLegacy(ruta: string): DocumentoInversionAdjuntoApi {
  const r = ruta.trim();
  return {
    ruta_almacenamiento: r,
    nombre_archivo: nombreArchivoDesdeRutaAlmacenamiento(r),
    mime_type: null,
    fecha_subida: null,
    tamano_bytes: null,
  };
}

function mergeAdjuntosInversionPorRutaMeta(items: DocumentoInversionAdjuntoApi[]): DocumentoInversionAdjuntoApi[] {
  const seen = new Set<string>();
  const out: DocumentoInversionAdjuntoApi[] = [];
  for (const it of items) {
    const r = it.ruta_almacenamiento.trim();
    if (!r || seen.has(r)) continue;
    seen.add(r);
    out.push({
      ...it,
      ruta_almacenamiento: r,
      nombre_archivo: it.nombre_archivo.trim() || nombreArchivoDesdeRutaAlmacenamiento(r),
    });
  }
  return out;
}

/** Lista de anexos: array de objetos metadata API o strings (legacy). */
function listaAnexosInversionDesdeCampoApi(raw: unknown): DocumentoInversionAdjuntoApi[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    if (raw.length === 0) return [];
    const first = raw[0];
    if (typeof first === 'object' && first !== null) {
      const keys = Object.keys(first as object);
      if (keys.includes('ruta_almacenamiento') || keys.includes('rutaAlmacenamiento')) {
        return mergeAdjuntosInversionPorRutaMeta(
          raw
            .map((x) => adjuntoInversionMetadataDesdeObjetoApi(x))
            .filter((x): x is DocumentoInversionAdjuntoApi => x != null)
        );
      }
    }
    const rutas = normalizarListaReferenciasAnexoDesdeApi(raw);
    return mergeAdjuntosInversionPorRutaMeta(rutas.map((ruta) => adjuntoMinimoDesdeRutaLegacy(ruta)));
  }
  const rutas = normalizarListaReferenciasAnexoDesdeApi(raw);
  return mergeAdjuntosInversionPorRutaMeta(rutas.map((ruta) => adjuntoMinimoDesdeRutaLegacy(ruta)));
}

/**
 * Lista de rutas de anexos desde el API: string CSV, array de strings, JSON array en string, o `;` como separador.
 * No usar con arrays de objetos metadata; para eso está `listaAnexosInversionDesdeCampoApi`.
 */
function normalizarListaReferenciasAnexoDesdeApi(raw: unknown): string[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map((x) => String(x ?? '').trim()).filter(Boolean);
  }
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return [];
    if (t.startsWith('[')) {
      try {
        const p = JSON.parse(t) as unknown;
        if (Array.isArray(p)) {
          return p.map((x) => String(x ?? '').trim()).filter(Boolean);
        }
      } catch {
        /* continuar con separadores */
      }
    }
    return t
      .split(/[,;]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

function resolverDocumentosPlanInversionEnDetalle(detalle: unknown): {
  documento_plan: string | null;
  documento_plan_nombre: string | null;
  documento_plan_adjunto: DocumentoInversionAdjuntoApi | null;
  rutas_anexos: string[];
  anexos_adjuntos: DocumentoInversionAdjuntoApi[];
} {
  const empty = {
    documento_plan: null as string | null,
    documento_plan_nombre: null as string | null,
    documento_plan_adjunto: null as DocumentoInversionAdjuntoApi | null,
    rutas_anexos: [] as string[],
    anexos_adjuntos: [] as DocumentoInversionAdjuntoApi[],
  };
  if (detalle == null || typeof detalle !== 'object') return empty;
  const root = detalle as Record<string, unknown>;
  const nested = root['plan_inversion'] ?? root['planInversion'];
  const plan =
    nested != null && typeof nested === 'object'
      ? (nested as Record<string, unknown>)
      : root;

  const pickPlanDoc = (raw: unknown): { ruta: string | null; nombre: string | null } => {
    const meta = adjuntoInversionMetadataDesdeObjetoApi(raw);
    if (meta) {
      return { ruta: meta.ruta_almacenamiento, nombre: meta.nombre_archivo };
    }
    const ruta =
      normalizarReferenciaDocumentoPlan(raw);
    if (ruta) {
      return { ruta, nombre: nombreArchivoDesdeRutaAlmacenamiento(ruta) };
    }
    return { ruta: null, nombre: null };
  };

  const desdePlan = pickPlanDoc(plan['documento_plan']);
  const desdeRoot = pickPlanDoc(root['documento_plan']);
  const docRuta = desdePlan.ruta ?? desdeRoot.ruta;
  const docNombre = desdePlan.nombre ?? desdeRoot.nombre ?? (docRuta ? nombreArchivoDesdeRutaAlmacenamiento(docRuta) : null);

  let documento_plan_adjunto: DocumentoInversionAdjuntoApi | null =
    adjuntoInversionMetadataDesdeObjetoApi(plan['documento_plan']) ??
    adjuntoInversionMetadataDesdeObjetoApi(root['documento_plan']);
  if (!documento_plan_adjunto && docRuta) {
    documento_plan_adjunto = {
      ruta_almacenamiento: docRuta,
      nombre_archivo: (docNombre ?? nombreArchivoDesdeRutaAlmacenamiento(docRuta)).trim(),
      mime_type: null,
      fecha_subida: null,
      tamano_bytes: null,
    };
  }

  /** Anexos: metadata en array, CSV en `documento_anexo`, o strings legacy. */
  const anexosDesde = (obj: Record<string, unknown>): DocumentoInversionAdjuntoApi[] => [
    ...listaAnexosInversionDesdeCampoApi(obj['documento_anexo']),
    ...listaAnexosInversionDesdeCampoApi(obj['documentos_anexos']),
    ...listaAnexosInversionDesdeCampoApi(obj['documentosAnexos']),
  ];
  const mergedAdjuntos = mergeAdjuntosInversionPorRutaMeta([...anexosDesde(plan), ...anexosDesde(root)]);
  const rutas_anexos = mergedAdjuntos.map((a) => a.ruta_almacenamiento);

  return {
    documento_plan: docRuta,
    documento_plan_nombre: docNombre,
    documento_plan_adjunto,
    rutas_anexos,
    anexos_adjuntos: mergedAdjuntos,
  };
}

/**
 * Rutas de documentos en `detalle_inversion` aunque falte el bloque completo parseable por `extraerPlanInversionDesdeDetalle`
 * (p. ej. API con `plan_inversion` anidado pero `documento_plan` solo en la raíz, o tipo/costo aún incompletos).
 */
export function extraerDocumentosPlanInversionDesdeDetalle(detalle: unknown): {
  /** `ruta_almacenamiento` para `GET /api/v1/archivos/{file_path}`. */
  documento_plan: string | null;
  /** `nombre_archivo` para mostrar en UI. */
  documento_plan_nombre: string | null;
  /** Objeto completo del plan en servidor (para `documento_plan_anterior` en PUT). */
  documento_plan_adjunto: DocumentoInversionAdjuntoApi | null;
  /** CSV de rutas (compat); preferir `anexos_adjuntos`. */
  documento_anexo: string | null;
  /** Metadatos por ítem de `documentos_anexos` (misma forma que exige el PUT en `documentos_anexos_anteriores`). */
  anexos_adjuntos: DocumentoInversionAdjuntoApi[];
} {
  const r = resolverDocumentosPlanInversionEnDetalle(detalle);
  return {
    documento_plan: r.documento_plan,
    documento_plan_nombre: r.documento_plan_nombre,
    documento_plan_adjunto: r.documento_plan_adjunto,
    documento_anexo: r.rutas_anexos.length ? r.rutas_anexos.join(',') : null,
    anexos_adjuntos: r.anexos_adjuntos,
  };
}

/**
 * Normaliza `detalle_inversion` del GET soluciones: acepta forma plana OpenAPI o anidada `{ plan_inversion: {...} }`.
 */
export function extraerPlanInversionDesdeDetalle(detalle: unknown): PlanInversionSchema | null {
  if (detalle == null || typeof detalle !== 'object') return null;
  const root = detalle as Record<string, unknown>;
  const nested = root['plan_inversion'] ?? root['planInversion'];
  const plan =
    nested != null && typeof nested === 'object'
      ? (nested as Record<string, unknown>)
      : root;
  const descripcion = plan['descripcion'];
  const tipoRaw = plan['tipo'];
  const costo_total = plan['costo_total'];
  const cronograma = plan['cronograma'];
  if (typeof descripcion !== 'string' || tipoRaw == null || typeof tipoRaw !== 'object') return null;
  const tipoObj = tipoRaw as Record<string, unknown>;
  const idTipo = parseIdTipoInversionApi(tipoObj['id']);
  const descTipo = tipoObj['descripcion'];
  if (idTipo == null || typeof descTipo !== 'string') return null;
  const costoNorm = normalizarCostoPlanApi(costo_total);
  const cronList = normalizarCronogramaApi(cronograma);
  if (costoNorm == null || !cronList || cronList.length === 0) return null;
  const docs = resolverDocumentosPlanInversionEnDetalle(detalle);
  return {
    descripcion,
    tipo: { id: idTipo, descripcion: descTipo },
    costo_total: costoNorm,
    cronograma: cronList,
    documento_plan: docs.documento_plan,
    documento_plan_nombre: docs.documento_plan_nombre,
    documento_anexo: docs.rutas_anexos.length ? docs.rutas_anexos.join(',') : null,
    documentos_anexos: docs.rutas_anexos.length ? [...docs.rutas_anexos] : null,
  };
}

/**
 * Item de solución por plan (GET /api/v1/negocio/{id_plan_negocio}/soluciones).
 * Sirve para las rutas: soluciones (todos), plan-trabajo (priorización 1 o 2 en la vista; incluye con y sin inversión), plan-inversion (requiere_inversion === true).
 */
export interface SolucionItem {
  /** UUID; opcional por compatibilidad con respuestas antiguas. */
  id_solucion?: string;
  /** Priorización del hallazgo (diagnóstico). En la vista Soluciones solo se listan 1 y 2. */
  id_priorizacion?: number | null;
  area_gestion: string;
  /** Algunas respuestas omiten este campo. */
  se_prioriza?: boolean;
  requiere_inversion: boolean;
  descripcion_hallazgo: string;
  fecha_inicio: string | null;
  fecha_termino: string | null;
  titulo?: string | null;
  /** Campo de observaciones desde el API (antes `tipo`). */
  observaciones?: string | null;
  /** Rubro de la solución (ej. { id_rubro: 4, descripcion: "Hortalizas" }). Usar en Soluciones Técnicas. */
  rubro?: RubroSolucion | null;
  /** Si no existe en la respuesta, mostrar "Sin categoría". */
  tipo_asesoria?: TipoAsesoria | null;
  /** Costo estimado (plan inversión); opcional si el API no lo expone aún. */
  costo_estimado?: number | null;

  /** Categorías PDAs asignadas a esta solución (para precargar selector múltiple). */
  pdaAsignadoSolucion?: PdaAsignadoSolucion[] | null;

  /** Actividades grupales asignadas a esta solución (para precargar selector múltiple). Clave API legacy. */
  AsesoriaEspecializadaAsignadaSolucion?: ActividadGrupalAsignadaSolucion[] | null;

  /**
   * Detalle de inversión cuando `requiere_inversion` es true (objeto libre o anidado según versión de API).
   */
  detalle_inversion?: unknown;
}

/**
 * Item de solución por plan (GET /api/v1/negocio/{id_plan_negocio}/soluciones).
 * Sirve para las rutas: soluciones (todos), plan-trabajo (priorización 1 o 2 en la vista; incluye con y sin inversión), plan-inversion (requiere_inversion === true).
 */
export interface VisitaPlanItem {
  id_visita: string | null;
  nombre: string | null;
  fecha_inicio: string | null;
  fecha_termino: string | null;
  id_tipo_visita: number | null;
  lista_soluciones: string[] | null;
  ciclo: number;
  id_estado_visita?: number | null;
}

/**
 * Servicio para el módulo Plan de Negocio (tag OpenAPI **Plan de Negocio**, rutas `/api/v1/negocio/...`).
 * Las solicitudes HTTP están en `SolicitudService` (`solicitud.service.ts`).
 */
@Injectable({
  providedIn: 'root'
})
export class NegocioService {
  private apiUrl = environment.apiUrl;
  /** Memo de `resolveArchivosApiBaseUrl()` (depende de `window` solo en el navegador). */
  private archivosApiBaseUrlMemo: string | null = null;

  /**
   * Base para `GET /api/v1/archivos/{file_path}` (OpenAPI tag Archivos).
   * - Sin `archivosApiUrl` en env: `apiUrl`.
   * - Con `archivosApiUrl` **http:** y página en **https:** → `apiUrl` (mixed content + satapp).
   * - Con `archivosApiUrl` **http:** y página en **http:** (p. ej. localhost) → override (CORS en apip-sat).
   */
  private resolveArchivosApiBaseUrl(): string {
    const api = environment.apiUrl.replace(/\/$/, '');
    const explicitRaw = (environment as { archivosApiUrl?: string }).archivosApiUrl?.trim();
    const explicit = explicitRaw ? explicitRaw.replace(/\/$/, '') : '';
    if (!explicit) return api;
    const pageHttps =
      typeof globalThis !== 'undefined' &&
      typeof (globalThis as unknown as Window).location?.protocol === 'string' &&
      (globalThis as unknown as Window).location.protocol === 'https:';
    if (pageHttps && explicit.toLowerCase().startsWith('http:')) {
      return api;
    }
    return explicit;
  }

  private getArchivosApiBaseUrl(): string {
    if (this.archivosApiBaseUrlMemo === null) {
      this.archivosApiBaseUrlMemo = this.resolveArchivosApiBaseUrl();
    }
    return this.archivosApiBaseUrlMemo;
  }
  /** Base pública de archivos subidos; se concatena con rutas de `documento_plan` / `documento_anexo`. */
  private uploadsUrl = environment.uploadsUrl ?? '';
  private readonly http = inject(HttpClient);

  /**
   * Obtiene el listado de productores con plan de negocio (paginado).
   * @param id_perfil ID del perfil activo (store)
   * @param id_jerarquia ID de jerarquía; -1 = todas las accesibles
   */
  obtenerProductoresPlanNegocio(
    id_perfil: number,
    id_jerarquia: number,
    params: NegocioQueryParams = {}
  ): Observable<ProductoresPlanNegocioPaginados> {
    let httpParams = new HttpParams();
    const page = params.page ?? 1;
    const size = params.size ?? 50;
    httpParams = httpParams.set('page', String(page)).set('size', String(size));
    if (params.sort) {
      httpParams = httpParams.set('sort', params.sort);
    }
    if (params.order) {
      httpParams = httpParams.set('order', params.order);
    }
    if (params.search?.trim()) {
      httpParams = httpParams.set('search', params.search.trim());
    }
    if (params.id_estado_plan_negocio != null) {
      httpParams = httpParams.set(
        'id_estado_plan_negocio',
        String(params.id_estado_plan_negocio)
      );
    }
    if (params.incluir_sin_estado_plan_negocio === true) {
      httpParams = httpParams.set('incluir_sin_estado_plan_negocio', 'true');
    }

    const url = `${this.apiUrl}/negocio/perfil/${id_perfil}/jerarquia/${id_jerarquia}`;
    return this.http.get<ProductoresPlanNegocioPaginados>(url, { params: httpParams });
  }

  /**
   * Obtiene un productor por id_productor desde el endpoint específico.
   * GET /api/v1/negocio/id_productor/{id_productor}
   */
  getProductorNegocioPorId(id_productor: string): Observable<ProductorNegocioPorIdResponse> {
    const id = (id_productor ?? '').trim();
    const url = `${this.apiUrl}/negocio/id_productor/${encodeURIComponent(id)}`;
    return this.http.get<ProductorNegocioPorIdResponse>(url);
  }

  /**
   * Catálogo de estados de plan de negocio para filtros de listado.
   * GET /api/v1/negocio/obtenerEstadosNegocio
   */
  obtenerEstadosNegocio(): Observable<EstadoPlanNegocioOpcion[]> {
    const url = `${this.apiUrl}/negocio/obtenerEstadosNegocio`;
    return this.http.get<EstadoPlanNegocioOpcion[]>(url);
  }

  /**
   * Catálogo de tipos de inversión para el plan de inversión.
   * GET /api/v1/negocio/obtenerTipoInversion
   */
  obtenerTipoInversion(): Observable<TipoInversionSchema[]> {
    const url = `${this.apiUrl}/negocio/obtenerTipoInversion`;
    return this.http.get<TipoInversionSchema[]>(url);
  }

  /**
   * Obtiene un productor por id_productor (para recuperar encabezado tras refresh).
   * Preferir endpoint específico (1 item): GET /api/v1/negocio/id_productor/{id_productor}
   * Fallback por compatibilidad: 1) Listado con búsqueda; 2) primera página grande; 3) caracterización (OpenAPI)
   * si no está en el listado (p. ej. otra página o criterios distintos a los de solicitudes).
   */
  obtenerProductorPorId(
    id_perfil: number,
    id_jerarquia: number,
    id_productor: string
  ): Observable<ProductorPlanNegocioItem | null> {
    const id = (id_productor ?? '').trim();
    if (!id) return of(null);

    const findInResponse = (resp: ProductoresPlanNegocioPaginados) =>
      resp.items?.find(p => p.id_productor === id_productor) ?? null;
    const withSearch: NegocioQueryParams = { page: 1, size: 50, search: id_productor };
    const withSize: NegocioQueryParams = { page: 1, size: 500, sort: 'nombre', order: 'asc' };

    const fallbackListado = (): Observable<ProductorPlanNegocioItem | null> =>
      this.obtenerProductoresPlanNegocio(id_perfil, id_jerarquia, withSearch).pipe(
        switchMap(resp => {
          const found = findInResponse(resp);
          if (found) return of(found);
          return this.obtenerProductoresPlanNegocio(id_perfil, id_jerarquia, withSize).pipe(
            switchMap(r => {
              const f = findInResponse(r);
              if (f) return of(f);
              return this.getCaracterizacion(id_productor).pipe(
                map(c => mapProductorCaracterizacionAPlanNegocioItem(c)),
                catchError(() => of(null))
              );
            })
          );
        })
      );

    return this.getProductorNegocioPorId(id).pipe(
      map(mapProductorNegocioPorIdAPlanNegocioItem),
      catchError(() => fallbackListado())
    );
  }

  /**
   * Obtiene la caracterización del productor.
   * GET /api/v1/negocio/productor/{id_productor}/caracterizacion
   */
  getCaracterizacion(id_productor: string): Observable<ProductorCaracterizacion> {
    const url = `${this.apiUrl}/negocio/productor/${id_productor}/caracterizacion`;
    return this.http.get<ProductorCaracterizacion>(url);
  }

  /**
   * Obtiene las soluciones técnicas (hallazgos por área de gestión) del plan de negocio.
   * GET /api/v1/negocio/{id_plan_negocio}/soluciones
   * @param id_plan_negocio UUID del plan de negocio (propiedad del productor: plan_negocio.id_plan_negocio)
   */
  getSoluciones(id_plan_negocio: string): Observable<SolucionItem[]> {
    const url = `${this.apiUrl}/negocio/${encodeURIComponent(id_plan_negocio)}/soluciones`;
    return this.http.get<SolucionItem[]>(url);
  }

  /**
   * Obtiene las visitas del plan de trabajo del plan de negocio.
   * GET /api/v1/negocio/{id_plan_negocio}/planTrabajo
   * @param id_plan_negocio UUID del plan de negocio (propiedad del productor: plan_negocio.id_plan_negocio)
   */
  getVisitasPlan(id_plan_negocio: string): Observable<VisitaPlanItem[]> {
    const url = `${this.apiUrl}/negocio/${encodeURIComponent(id_plan_negocio)}/planTrabajo`;
    return this.http.get<VisitaPlanItem[]>(url);
  }

  /**
   * Obtiene el diagnóstico asociado a un plan de negocio.
   * Endpoint: GET /api/v1/negocio/{id_plan_negocio}/obtenerDiagnostico
   */
  obtenerDiagnosticoPlan(idPlanNegocio: string): Observable<unknown> {
    const url = `${this.apiUrl}/negocio/${encodeURIComponent(idPlanNegocio)}/obtenerDiagnostico`;
    return this.http.get<unknown>(url);
  }

  /**
   * Actualiza en bloque las soluciones del plan de negocio.
   * OpenAPI: PUT /api/v1/negocio/{id_plan_negocio}/soluciones
   */
  actualizarSolucionesPlan(
    idPlanNegocio: string,
    payload: BulkUpdateSolucionesRequest
  ): Observable<unknown> {
    const url = `${this.apiUrl}/negocio/${encodeURIComponent(idPlanNegocio)}/soluciones`;
    return this.http.put<unknown>(url, payload);
  }

  /**
   * Guarda el plan de inversión de una solución.
   * PUT `/api/v1/negocio/actualizaInversion`: query (id_solucion, descripcion, id_tipo, tipo, costo_total, cronograma)
   * + `multipart/form-data`: `documento_plan`, `documento_plan_anterior` (JSON objeto), `documentos_anexos`, `documentos_anexos_anteriores`.
   */
  actualizarInversionSolucion(payload: ActualizaInversionPutParams): Observable<unknown> {
    const url = `${this.apiUrl}/negocio/actualizaInversion`;
    const p = payload;
    let params = new HttpParams().set('id_solucion', p.id_solucion.trim());
    if (p.descripcion !== undefined && p.descripcion !== null) {
      params = params.set('descripcion', String(p.descripcion));
    }
    if (p.id_tipo !== undefined && p.id_tipo !== null && Number.isFinite(Number(p.id_tipo))) {
      params = params.set('id_tipo', String(Math.round(Number(p.id_tipo))));
    }
    if (p.tipo !== undefined && p.tipo !== null) {
      params = params.set('tipo', String(p.tipo));
    }
    if (p.costo_total !== undefined && p.costo_total !== null && Number.isFinite(Number(p.costo_total))) {
      params = params.set('costo_total', String(Math.round(Number(p.costo_total))));
    }

    const form = new FormData();

    // Nuevo contrato: cronograma en body multipart como array[int] (múltiples valores 'cronograma').
    if (p.cronograma !== undefined && p.cronograma !== null) {
      const arr = Array.isArray(p.cronograma) ? p.cronograma : [];
      for (const v of arr) {
        const n = typeof v === 'number' ? v : Number(v);
        if (!Number.isFinite(n)) continue;
        form.append('cronograma', String(Math.round(n)));
      }
    }

    if (p.documento_plan_anterior !== undefined) {
      if (p.documento_plan_anterior === null) {
        form.append('documento_plan_anterior', 'null');
      } else if ((p.documento_plan_anterior.ruta_almacenamiento ?? '').trim()) {
        form.append(
          'documento_plan_anterior',
          serializarAdjuntoInversionFormBody(p.documento_plan_anterior)
        );
      }
    }

    if (p.documentos_anexos_anteriores !== undefined) {
      const arr = p.documentos_anexos_anteriores ?? [];
      for (const d of arr) {
        if (!(d?.ruta_almacenamiento ?? '').trim()) continue;
        form.append('documentos_anexos_anteriores', serializarAdjuntoInversionFormBody(d));
      }
    }

    if (p.documento_plan instanceof File) {
      form.append('documento_plan', p.documento_plan, p.documento_plan.name);
    }

    const anexos = p.documentos_anexos?.filter((f): f is File => f instanceof File) ?? [];
    for (const f of anexos) {
      form.append('documentos_anexos', f, f.name);
    }

    return this.http.put<unknown>(url, form, { params });
  }

  /**
   * Actualiza en bloque fechas de inicio y término de soluciones del plan de negocio.
   * OpenAPI: PUT /api/v1/negocio/actualizaFechasPlan — BulkFechasRequest
   */
  actualizarFechasPlan(payload: BulkFechasRequest): Observable<unknown> {
    const url = `${this.apiUrl}/negocio/actualizaFechasPlan`;
    return this.http.put<unknown>(url, payload);
  }
  actualizarFechasPlanVisitas(payload: BulkFechasVisitasRequest): Observable<unknown> {
    const url = `${this.apiUrl}/negocio/planTrabajoVisitas`;
    return this.http.post<unknown>(url, payload);
  }

  /**
   * Avance por secciones del plan (0..100) + total, calculado en el backend.
   * OpenAPI: GET /api/v1/negocio/{id_plan_negocio}/avance
   */
  obtenerAvancePlan(idPlanNegocio: string): Observable<DetalleAvancePlanNegocio> {
    const id = (idPlanNegocio ?? '').trim();
    const url = `${this.apiUrl}/negocio/${encodeURIComponent(id)}/avance`;
    return this.http.get<DetalleAvancePlanNegocio>(url);
  }

  /**
   * Versiones históricas del plan disponibles para PDF.
   * OpenAPI: GET /api/v1/negocio/{id_plan_negocio}/listaPdf
   */
  obtenerListaPdfPlanNegocio(idPlanNegocio: string): Observable<ListaPdfPlanNegocioItem[]> {
    const id = (idPlanNegocio ?? '').trim();
    const url = `${this.apiUrl}/negocio/${encodeURIComponent(id)}/listaPdf`;
    return this.http.get<ListaPdfPlanNegocioItem[]>(url);
  }

  /**
   * Estructura completa (por secciones) del plan de negocio.
   * OpenAPI: GET /api/v1/negocio/{id_plan_negocio}/estructura — `EstructuraPlan`.
   * Con `opts.fecha`, el backend devuelve la estructura archivada para esa marca temporal.
   */
  obtenerEstructuraPlanNegocio(
    idPlanNegocio: string,
    opts?: ObtenerEstructuraPlanNegocioOpts
  ): Observable<EstructuraPlan> {
    const id = (idPlanNegocio ?? '').trim();
    const url = `${this.apiUrl}/negocio/${encodeURIComponent(id)}/estructura`;
    const fecha = (opts?.fecha ?? '').trim();
    if (!fecha) {
      return this.http.get<EstructuraPlan>(url);
    }
    const params = new HttpParams().set('fecha', fecha);
    return this.http.get<EstructuraPlan>(url, { params });
  }

  /**
   * URL absoluta para visualizar un archivo guardado en `documento_plan` o `documento_anexo`:
   * `uploadsUrl` (env) + ruta devuelta por el API (ej. `/uploads/hand-cursor.png` o `hand-cursor.png`).
   * Evita duplicar el segmento `uploads` si la base ya termina en `/uploads`.
   */
  construirUrlDocumentoInversionUploads(rutaReferencia: string | null | undefined): string {
    const raw = (rutaReferencia ?? '').trim();
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw)) {
      return raw;
    }
    const base = this.uploadsUrl.replace(/\/$/, '');
    if (!base) {
      return '';
    }
    let path = raw.replace(/^\/+/, '');
    const baseLower = base.toLowerCase();
    if (baseLower.endsWith('/uploads') && path.toLowerCase().startsWith('uploads/')) {
      path = path.slice('uploads/'.length);
    }
    return `${base}/${path}`;
  }

  /**
   * Codifica `file_path` para GET `/api/v1/archivos/{file_path}`.
   * El backend trata el resto del path como un solo parámetro: las barras van como `%2F`
   * (p. ej. `../uploads/{id}/f.jpg` → `..%2Fuploads%2F...`), no como segmentos de URL sin codificar.
   */
  private encodeRutaArchivoApi(filePath: string): string {
    const raw = filePath.trim();
    if (!raw) return '';
    return encodeURIComponent(raw);
  }

  /**
   * Descarga el binario de un archivo almacenado (p. ej. `documento_plan`, ítems de `documentos_anexos`).
   * OpenAPI: GET `/api/v1/archivos/{file_path}` — requiere Bearer (interceptor).
   */
  obtenerArchivoPorRuta(filePath: string | null | undefined): Observable<Blob> {
    const encoded = this.encodeRutaArchivoApi(filePath ?? '');
    if (!encoded) {
      return throwError(() => new Error('Ruta de archivo vacía'));
    }
    const url = `${this.getArchivosApiBaseUrl()}/archivos/${encoded}`;
    return this.http.get(url, { responseType: 'blob' }).pipe(
      catchError(err => {
        console.error('Error al obtener archivo por ruta:', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Obtiene las categorías PDA para el formulario.
   * OpenAPI: GET /api/v1/negocio/obtenerCategoriasPda
   */
  obtenerCategoriasPda(): Observable<CategoriaPdaItem[]> {
    const url = `${this.apiUrl}/negocio/obtenerCategoriasPda`;
    return this.http.get<CategoriaPdaItem[]>(url).pipe(
      catchError(error => {
        console.error('Error al obtener categorías PDA:', error);
        return of([]);
      })
    );
  }

  /**
   * Obtiene el listado de priorizaciones para diagnóstico.
   * OpenAPI: GET /api/v1/negocio/obtenerPriorizacion
   */
  obtenerPriorizacion(): Observable<PriorizacionResponse[]> {
    const url = `${this.apiUrl}/negocio/obtenerPriorizacion`;
    return this.http.get<PriorizacionResponse[]>(url);
  }

  /**
   * Actualiza priorizaciones del diagnóstico.
   * POST /api/v1/negocio/actualizaPriorizacion
   *
   * Nota: el backend acepta distintos formatos de body según si existe plan o no.
   */
  actualizaPriorizacion(payload: unknown): Observable<unknown> {
    const url = `${this.apiUrl}/negocio/actualizaPriorizacion`;
    return this.http.post<unknown>(url, payload);
  }

  /**
   * Obtiene asesorías especializadas para el selector, filtradas por jerarquía del productor.
   * OpenAPI: GET /api/v1/negocio/jerarquia/{id_jerarquia_productor}/obtenerAsesoriasEspecializadas
   */
  obtenerActividadesGrupalesPorJerarquia(
    idJerarquiaProductor: number
  ): Observable<ActividadGrupalOpcionSoluciones[]> {
    const url = `${this.apiUrl}/negocio/jerarquia/${encodeURIComponent(idJerarquiaProductor)}/obtenerAsesoriasEspecializadas`;
    return this.http.get<ActividadGrupalOpcionSoluciones[]>(url).pipe(
      catchError(error => {
        console.error('Error al obtener asesorías especializadas por jerarquía del productor:', error);
        return of([]);
      })
    );
  }

  // --- SEGUIMIENTO DE INVERSIÓN ---

  /**
   * Obtiene los datos de seguimiento de inversión para un plan de negocio.
   * GET /api/v1/seguimiento/{id_plan_negocio}
   */
  obtenerSeguimientoInversion(id_plan_negocio: string): Observable<any> {
    const url = `${this.apiUrl}/seguimiento/${id_plan_negocio}`;
    return this.http.get<any>(url);
  }

  /**
   * Guarda de forma masiva el seguimiento de inversión.
   * POST /api/v1/seguimiento/{id_plan_negocio}/guardar
   */
  guardarSeguimientoInversion(id_plan_negocio: string, soluciones: any[]): Observable<any> {
    const url = `${this.apiUrl}/seguimiento/${id_plan_negocio}/guardar`;
    return this.http.post<any>(url, { soluciones });
  }

  obtenerCopago(idPlanNegocio: string): Observable<{ copago: boolean; copago_2: boolean }> {
    const url = `${this.apiUrl}/negocio/${encodeURIComponent(idPlanNegocio)}/copago`;
    return this.http.get<{ copago: boolean; copago_2: boolean }>(url);
  }

  actualizarCopago(idPlanNegocio: string, copago: boolean, copago_2: boolean): Observable<any> {
    const url = `${this.apiUrl}/negocio/${encodeURIComponent(idPlanNegocio)}/copago`;
    return this.http.put<any>(url, { copago, copago_2 });
  }

  /**
   * Cierra directamente el plan de negocio (Ejecutivo)
   * POST /api/v1/negocio/{id_plan_negocio}/cerrar-directo
   */
  cerrarPlanDirecto(idPlanNegocio: string, motivo: string, comentario: string): Observable<{ status: string; id_estado_plan_negocio: number }> {
    const url = `${this.apiUrl}/negocio/${encodeURIComponent(idPlanNegocio)}/cerrar-directo`;
    return this.http.post<{ status: string; id_estado_plan_negocio: number }>(url, { motivo, comentario });
  }

  /**
   * Obtiene la lista de todos los planes de negocio de un productor.
   * GET /api/v1/negocio/productor/{id_productor}/planes
   */
  obtenerPlanesProductor(idProductor: string): Observable<PlanNegocioHistoricoItem[]> {
    const url = `${this.apiUrl}/negocio/productor/${encodeURIComponent(idProductor)}/planes`;
    return this.http.get<PlanNegocioHistoricoItem[]>(url);
  }

}

export interface PlanNegocioHistoricoItem {
  id_plan_negocio: string;
  id_estado_plan_negocio?: number | null;
  estado?: string | null;
  fecha_inicio?: string | null;
  fecha_termino?: string | null;
  detalle_avance?: DetalleAvancePlanNegocio | null;
  copago?: boolean;
  copago_2?: boolean;
}


